import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, MapPin, Pause, Play } from 'lucide-react';
import { heroSlides } from '../../../data/events';

export function HeroSlider() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const count = heroSlides.length;

  const go = useCallback(
    (direction: number) => setActive((current) => (current + direction + count) % count),
    [count],
  );

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (paused || hovered || reducedMotion) return;
    const id = window.setInterval(() => setActive((current) => (current + 1) % count), 6500);
    return () => window.clearInterval(id);
  }, [count, hovered, paused, reducedMotion]);

  const slide = heroSlides[active];
  const background = useMemo(
    () =>
      `linear-gradient(90deg, rgba(9,11,17,0.92) 0%, rgba(9,11,17,0.66) 54%, rgba(9,11,17,0.28) 100%), url('${slide.image}')`,
    [slide.image],
  );

  return (
    <section
      aria-label="Featured events"
      aria-roledescription="carousel"
      className="relative min-h-[min(40rem,100svh)] overflow-hidden bg-ink"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        key={slide.name}
        className="hero-slide-enter absolute inset-0 flex items-center bg-cover"
        style={{ backgroundImage: background, backgroundPosition: slide.position ?? 'center' }}
      >
        <div className="mx-auto w-full max-w-content px-6 py-28 md:px-16">
          <div className="max-w-[680px]" aria-live="polite" aria-atomic="true">
            <span className="inline-block rounded-sm bg-action px-3 py-1.5 font-ui text-xs font-semibold uppercase tracking-[0.14em] text-action-foreground">
              {slide.eyebrow}
            </span>
            <h1 className="mt-6 font-display text-5xl font-bold leading-[1.02] tracking-tight text-white sm:text-6xl md:text-[4.25rem]">
              {slide.name}
            </h1>
            <div className="mt-5 flex items-start gap-2.5 text-white/90">
              <MapPin size={18} strokeWidth={1.75} className="mt-1 shrink-0 text-action" aria-hidden="true" />
              <span className="font-body text-lg">{slide.venue}</span>
            </div>
            <a
              href={slide.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-9 inline-flex items-center gap-2 rounded-sm bg-action px-7 py-3.5 font-ui text-sm font-semibold text-action-foreground transition-colors hover:bg-action-hover"
            >
              {slide.ctaLabel}
              <ArrowRight
                size={17}
                strokeWidth={2}
                className="transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </a>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-7 z-10 flex items-center justify-center gap-2.5">
        {heroSlides.map((item, index) => (
          <button
            key={item.name}
            type="button"
            onClick={() => setActive(index)}
            aria-label={`Show ${item.name}`}
            aria-current={index === active ? 'true' : undefined}
            className={
              'h-2 rounded-full transition-all duration-300 ' +
              (index === active ? 'w-8 bg-white' : 'w-2.5 bg-white/45 hover:bg-white/75')
            }
          />
        ))}
        <button
          type="button"
          onClick={() => setPaused((value) => !value)}
          className="ml-2 flex h-8 w-8 items-center justify-center rounded-full border border-white/50 text-white hover:bg-white/15"
          aria-label={paused ? 'Play featured events' : 'Pause featured events'}
          aria-pressed={paused}
        >
          {paused ? <Play size={14} fill="currentColor" /> : <Pause size={14} fill="currentColor" />}
        </button>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="Previous featured event"
        className="absolute left-5 top-1/2 z-10 -mt-6 hidden h-12 w-12 items-center justify-center rounded-full border border-white/45 text-white transition-colors hover:bg-white/15 md:flex"
      >
        <ChevronLeft size={22} strokeWidth={1.75} />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="Next featured event"
        className="absolute right-5 top-1/2 z-10 -mt-6 hidden h-12 w-12 items-center justify-center rounded-full border border-white/45 text-white transition-colors hover:bg-white/15 md:flex"
      >
        <ChevronRight size={22} strokeWidth={1.75} />
      </button>
    </section>
  );
}
